'use client';

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';

export interface StickyBottomBarProps {
  /** Child components to render inside the bar (e.g. CopyPage dropdown) */
  children: ReactNode;
  /**
   * CSS selector for the element that triggers showing the bar
   * once the user scrolls past it. Default: '.hero-region'
   */
  showAfterSelector?: string;
  /**
   * CSS selector for the element that triggers hiding the bar
   * when the user scrolls into the post-article area. Default: '.post-article'
   */
  hideAtSelector?: string;
}

/* ── Brightness detection ─────────────────────────────────────────── */

// Cache drawn image canvases so we only drawImage once per <img>.
// Videos are NOT cached — their current frame changes continuously.
const imgCanvasCache = new WeakMap<HTMLImageElement, HTMLCanvasElement>();

/** Scale factor — we don't need full-res, just enough for brightness. */
const SAMPLE_SCALE = 0.25;

/** Shared canvas for video frame sampling (reused every frame). */
let videoCanvas: HTMLCanvasElement | null = null;

function getImgCanvas(img: HTMLImageElement): HTMLCanvasElement | null {
  const cached = imgCanvasCache.get(img);
  if (cached) return cached;
  if (!img.complete || img.naturalWidth === 0) return null;

  try {
    const w = Math.round(img.naturalWidth * SAMPLE_SCALE);
    const h = Math.round(img.naturalHeight * SAMPLE_SCALE);
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    // Test that we can read pixels (same-origin check)
    ctx.getImageData(0, 0, 1, 1);
    imgCanvasCache.set(img, c);
    return c;
  } catch {
    // Cross-origin or tainted canvas — can't sample
    return null;
  }
}

function getVideoCanvas(video: HTMLVideoElement): HTMLCanvasElement | null {
  if (video.readyState < 2 || video.videoWidth === 0) return null;
  try {
    const w = Math.round(video.videoWidth * SAMPLE_SCALE);
    const h = Math.round(video.videoHeight * SAMPLE_SCALE);
    if (!videoCanvas) {
      videoCanvas = document.createElement('canvas');
    }
    videoCanvas.width = w;
    videoCanvas.height = h;
    const ctx = videoCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    return videoCanvas;
  } catch {
    return null;
  }
}

/** Sample the perceived brightness (0–1) at a single viewport point. */
function sampleBrightnessAt(
  x: number,
  y: number,
  bar: HTMLElement,
): number {
  // Find the first element behind the bar at this point.
  const elements = document.elementsFromPoint(x, y);
  const el = elements.find((e) => !bar.contains(e) && e !== bar);
  if (!el) return 0;

  // Check if it's (or is inside) an image or video
  const img =
    el.tagName === 'IMG'
      ? (el as HTMLImageElement)
      : (el.closest('figure, .wide-breakout')?.querySelector('img') as HTMLImageElement | null);

  const video =
    !img && el.tagName === 'VIDEO'
      ? (el as HTMLVideoElement)
      : !img
        ? (el.closest('figure, .wide-breakout')?.querySelector('video') as HTMLVideoElement | null)
        : null;

  // Resolve a canvas + source rect for either img or video
  const media = img || video;
  const canvas = img
    ? getImgCanvas(img)
    : video
      ? getVideoCanvas(video)
      : null;

  if (media && canvas) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      const mediaRect = media.getBoundingClientRect();
      const ix = Math.round(
        ((x - mediaRect.left) / mediaRect.width) * canvas.width,
      );
      const iy = Math.round(
        ((y - mediaRect.top) / mediaRect.height) * canvas.height,
      );
      const clamped_x = Math.max(0, Math.min(canvas.width - 1, ix));
      const clamped_y = Math.max(0, Math.min(canvas.height - 1, iy));
      const pixel = ctx.getImageData(clamped_x, clamped_y, 1, 1).data;
      // Perceived brightness (BT.601 luma)
      return (pixel[0] * 0.299 + pixel[1] * 0.587 + pixel[2] * 0.114) / 255;
    }
  }

  // No image — walk up the DOM for a CSS background-color
  let target: Element | null = el;
  while (target && target !== document.documentElement) {
    const bg = getComputedStyle(target).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (match) {
        return (
          (parseInt(match[1]) * 0.299 +
            parseInt(match[2]) * 0.587 +
            parseInt(match[3]) * 0.114) /
          255
        );
      }
    }
    target = target.parentElement;
  }

  return 0; // Assume dark if nothing found
}

/** Sample 5 points across the bar and return the average brightness. */
function measureBrightnessBehindBar(bar: HTMLElement): number {
  const rect = bar.getBoundingClientRect();
  const cy = rect.top + rect.height / 2;
  const pad = rect.width * 0.1;
  const points = [
    rect.left + pad,
    rect.left + rect.width * 0.3,
    rect.left + rect.width * 0.5,
    rect.left + rect.width * 0.7,
    rect.right - pad,
  ];

  let sum = 0;
  for (const px of points) {
    sum += sampleBrightnessAt(px, cy, bar);
  }
  return sum / points.length;
}

/* ── Component ────────────────────────────────────────────────────── */

const BRIGHTNESS_THRESHOLD = 0.55;

export function StickyBottomBar({
  children,
  showAfterSelector = '.hero-region',
  hideAtSelector = '.post-article',
}: StickyBottomBarProps) {
  const [visible, setVisible] = useState(false);
  const [lightBg, setLightBg] = useState(false);
  const pastHeroRef = useRef(false);
  const inFooterRef = useRef(false);
  const barRef = useRef<HTMLDivElement>(null);

  // Visibility observers (unchanged from before)
  useEffect(() => {
    const heroEl = document.querySelector(showAfterSelector);
    const footerEl = document.querySelector(hideAtSelector);

    if (!heroEl) return;

    const update = () => {
      setVisible(pastHeroRef.current && !inFooterRef.current);
    };

    const heroObserver = new IntersectionObserver(
      ([entry]) => {
        pastHeroRef.current = !entry.isIntersecting;
        update();
      },
      { threshold: 0 },
    );
    heroObserver.observe(heroEl);

    let footerObserver: IntersectionObserver | null = null;
    if (footerEl) {
      footerObserver = new IntersectionObserver(
        ([entry]) => {
          inFooterRef.current = entry.isIntersecting;
          update();
        },
        { threshold: 0 },
      );
      footerObserver.observe(footerEl);
    }

    return () => {
      heroObserver.disconnect();
      footerObserver?.disconnect();
    };
  }, [showAfterSelector, hideAtSelector]);

  // Brightness detection — only runs while bar is visible
  const sample = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    const brightness = measureBrightnessBehindBar(bar);
    setLightBg(brightness > BRIGHTNESS_THRESHOLD);
  }, []);

  useEffect(() => {
    if (!visible) return;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(sample);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Initial sample after a short delay so images have rendered
    raf = requestAnimationFrame(sample);

    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [visible, sample]);

  const className = [
    'sticky-bottom-bar',
    visible && 'sticky-bottom-bar--visible',
    lightBg && 'sticky-bottom-bar--light-bg',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={barRef}
      className={className}
      role="toolbar"
      aria-label="Article actions"
      style={{
        backdropFilter: 'blur(20px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(20px) saturate(1.2)',
      }}
    >
      <div className="sticky-bottom-bar-inner">{children}</div>
    </div>
  );
}
